import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { ThemeName, LayoutName, defaultConfig } from '@/config/siteConfig';

interface ThemeContextValue {
  theme: ThemeName;
  layout: LayoutName;
  setTheme: (theme: ThemeName) => void;
  setLayout: (layout: LayoutName) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeName>(defaultConfig.currentTheme);
  const [layout, setLayoutState] = useState<LayoutName>(defaultConfig.currentLayout);

  // Fetch theme/layout from database
  const { data: siteContent, isLoading } = useQuery({
    queryKey: ['site-content-theme'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('site_content')
        .select('current_theme, current_layout')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching theme:', error);
        return null;
      }

      return data;
    },
    staleTime: 1000 * 30, // 30 seconds cache
    refetchOnWindowFocus: true,
  });

  // Sync theme from database
  useEffect(() => {
    if (siteContent?.current_theme) {
      const dbTheme = siteContent.current_theme as ThemeName;
      setThemeState(dbTheme);
      document.documentElement.setAttribute('data-theme', dbTheme);
    }
  }, [siteContent?.current_theme]);

  // Sync layout from database
  useEffect(() => {
    if (siteContent?.current_layout) {
      setLayoutState(siteContent.current_layout as LayoutName);
    }
  }, [siteContent?.current_layout]);

  const setTheme = useCallback((newTheme: ThemeName) => {
    setThemeState(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  }, []);

  const setLayout = useCallback((newLayout: LayoutName) => {
    setLayoutState(newLayout);
  }, []);

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, layout, setTheme, setLayout, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
